import { roundToWhole } from "utils";

import { CM_LINE_CATEGORY_ORDER, CM_LINE_MAP, CM_LINE_RECONCILE_TOLERANCE, CM_LINE_SAME_TOLERANCE, CM_LINE_SOURCES } from "consts";

import type { CmLineCategory, CmLineDefinition, CmLineSubCategory, ContributionMarginYearBreakdown } from "models";

import { bandAvg } from "pages/app/scenario-analysis/utils/minAvgMax";

type YearRow = Pick<ContributionMarginYearBreakdown, "year" | "cropId" | "revenue" | "costs" | "contributionMargin">;

/** A line's value per year for one scenario: revenue positive, costs negative, trends split out. */
export type CmLineValuesByYear = Record<string, Map<number, number>>;

export type CmLineRow = {
  definition: CmLineDefinition;
  totalA: number;
  totalB: number;
  difference: number;
  labelKey: string;
  isSameInBoth: boolean;
};

export type CmSubCategoryRow = {
  subCategory: CmLineSubCategory;
  lines: CmLineRow[];
  totalA: number;
  totalB: number;
  difference: number;
  isSameInBoth: boolean;
};

export type CmCategoryRow = {
  category: CmLineCategory;
  subCategories: CmSubCategoryRow[];
  totalA: number;
  totalB: number;
  difference: number;
};

export type CmYearRow = { year: number; cropIdA: string | null; cropIdB: string | null; cmA: number; cmB: number; difference: number };

export type CmLineBreakdown = {
  categories: CmCategoryRow[];
  cm: { totalA: number; totalB: number; difference: number };
  years: CmYearRow[];
  /** The years both scenarios have; totals and the year table cover these only. */
  horizonYears: number;
  /** Every shown year's lines sum to the CM the API returned, within CM_LINE_RECONCILE_TOLERANCE. */
  isReconciled: boolean;
};

const termsValue = (terms: CmLineDefinition["terms"], row: YearRow): number =>
  terms.reduce((sum, { source, sign }) => sum + sign * (CM_LINE_SOURCES[source](row) ?? 0), 0);

// Each trend is shown once on its own line: every line hands back the share of it the API reports, so the lines still sum to the CM.
export const buildCmLineValues = (years: YearRow[], definitions: readonly CmLineDefinition[] = CM_LINE_MAP): CmLineValuesByYear => {
  const byLine: CmLineValuesByYear = Object.fromEntries(definitions.map((definition) => [definition.id, new Map<number, number>()]));
  years.forEach((row) => {
    definitions.forEach((definition) => {
      const trendShare = definition.trendShares ? termsValue(definition.trendShares, row) : 0;
      byLine[definition.id]?.set(row.year, termsValue(definition.terms, row) + trendShare);
    });
  });
  return byLine;
};

/** The difference as the row shows it: the rounded a minus the rounded b. */
const shownDifference = (totalA: number, totalB: number): number => roundToWhole(totalA) - roundToWhole(totalB);

const sumOf = (valuesByYear: Map<number, number> | undefined): number => Array.from(valuesByYear?.values() ?? []).reduce((sum, value) => sum + value, 0);

/** Every year within the tolerance; a year one scenario lacks counts as 0. */
const isSameInEveryYear = (a: Map<number, number>, b: Map<number, number>): boolean => {
  const years = new Set([...a.keys(), ...b.keys()]);
  return Array.from(years).every((year) => Math.abs((a.get(year) ?? 0) - (b.get(year) ?? 0)) < CM_LINE_SAME_TOLERANCE);
};

const isZeroEverywhere = (a: Map<number, number>, b: Map<number, number>): boolean => [...a.values(), ...b.values()].every((value) => Math.abs(value) < 0.005);

/** The directional form follows the difference as it is shown, so a difference that rounds to zero reads as neutral. */
export const cmLineLabelKey = (definition: CmLineDefinition, difference: number): string => {
  if (typeof definition.label === "string") return definition.label;
  const shown = roundToWhole(difference);
  if (shown > 0) return definition.label.up;
  if (shown < 0) return definition.label.down;
  return definition.label.same;
};

/** Per year, the gap between the shown lines and the CM the API returned; all should sit within CM_LINE_RECONCILE_TOLERANCE. */
export const cmLineReconciliationGaps = (
  years: YearRow[],
  definitions: readonly CmLineDefinition[] = CM_LINE_MAP,
  values: CmLineValuesByYear = buildCmLineValues(years, definitions)
): { year: number; gap: number }[] =>
  years.map((row) => {
    const shown = definitions.reduce((sum, definition) => sum + (values[definition.id]?.get(row.year) ?? 0), 0);
    return { year: row.year, gap: shown - bandAvg(row.contributionMargin?.total) };
  });

// An API without the breakdown fields can still add up (a missing tillage line just lands in other field work), so their presence is checked too.
const hasBreakdownFields = (row: YearRow): boolean =>
  row.costs?.tillage !== undefined && row.costs?.inputCostTrendShares !== undefined && row.revenue?.priceTrendShares !== undefined;

const isReconciled = (years: YearRow[], definitions: readonly CmLineDefinition[], values: CmLineValuesByYear): boolean =>
  years.every(hasBreakdownFields) && cmLineReconciliationGaps(years, definitions, values).every(({ gap }) => Math.abs(gap) <= CM_LINE_RECONCILE_TOLERANCE);

/**
 * Scenario a is the one compared; b is the one it is compared against. Differences are a minus b, over the years both have.
 * Totals stay unrounded and are rounded once for display, so the CM matches the Detailed calculations table;
 * a difference is the rounded a minus the rounded b, so every row reads right on screen.
 */
export const buildCmLineBreakdown = (allYearsA: YearRow[], allYearsB: YearRow[], definitions: readonly CmLineDefinition[] = CM_LINE_MAP): CmLineBreakdown => {
  const yearsInB = new Set(allYearsB.map((row) => row.year));
  const yearsA = allYearsA.filter((row) => yearsInB.has(row.year));
  const sharedYears = new Set(yearsA.map((row) => row.year));
  const yearsB = allYearsB.filter((row) => sharedYears.has(row.year));

  const valuesA = buildCmLineValues(yearsA, definitions);
  const valuesB = buildCmLineValues(yearsB, definitions);

  const lineRows: CmLineRow[] = definitions.flatMap((definition) => {
    const a = valuesA[definition.id] ?? new Map<number, number>();
    const b = valuesB[definition.id] ?? new Map<number, number>();
    if (!definition.keepWhenZero && isZeroEverywhere(a, b)) return [];
    const totalA = sumOf(a);
    const totalB = sumOf(b);
    const difference = shownDifference(totalA, totalB);
    return [{ definition, totalA, totalB, difference, labelKey: cmLineLabelKey(definition, difference), isSameInBoth: isSameInEveryYear(a, b) }];
  });

  const categories = CM_LINE_CATEGORY_ORDER.map((category) => {
    const subCategoryOrder = Array.from(
      new Set(definitions.filter((definition) => definition.category === category).map((definition) => definition.subCategory))
    );
    const subCategories = subCategoryOrder.flatMap((subCategory) => {
      const lines = lineRows.filter((row) => row.definition.category === category && row.definition.subCategory === subCategory);
      if (!lines.length) return [];
      const totalA = lines.reduce((sum, row) => sum + row.totalA, 0);
      const totalB = lines.reduce((sum, row) => sum + row.totalB, 0);
      return [{ subCategory, lines, totalA, totalB, difference: shownDifference(totalA, totalB), isSameInBoth: lines.every((row) => row.isSameInBoth) }];
    });
    const totalA = subCategories.reduce((sum, row) => sum + row.totalA, 0);
    const totalB = subCategories.reduce((sum, row) => sum + row.totalB, 0);
    return { category, subCategories, totalA, totalB, difference: shownDifference(totalA, totalB) };
  });

  const rowsB = new Map(yearsB.map((row) => [row.year, row]));
  const years = yearsA.map((row) => {
    const cmA = bandAvg(row.contributionMargin?.total);
    const rowB = rowsB.get(row.year);
    const cmB = bandAvg(rowB?.contributionMargin?.total);
    return { year: row.year, cropIdA: row.cropId, cropIdB: rowB?.cropId ?? null, cmA, cmB, difference: shownDifference(cmA, cmB) };
  });
  // The API's own CM, as the Detailed calculations table sums it, not the sum of the lines.
  const cmTotalA = years.reduce((sum, row) => sum + row.cmA, 0);
  const cmTotalB = years.reduce((sum, row) => sum + row.cmB, 0);

  return {
    categories,
    cm: { totalA: cmTotalA, totalB: cmTotalB, difference: shownDifference(cmTotalA, cmTotalB) },
    years,
    horizonYears: years.length,
    isReconciled: isReconciled(yearsA, definitions, valuesA) && isReconciled(yearsB, definitions, valuesB),
  };
};
